/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRouteFormatter, getApiKey, getApiVersion, isCloudRunEnabled } from "../ApiWeb";
import { Network } from "./network";

type IApiModelfile = ApiDataModel.IModelfile;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentModelfilesHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedModelfileHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onModelfileSelected: Function | undefined = undefined;
let _currentModelfiles: IApiModelfile[] = [];

export module UIModelfile {
    export function Bind(accessToken: string, workspaceId: string, modelgroupId: string, onModelfileSelected: Function): void {
        _accessToken = accessToken;
        _onModelfileSelected = onModelfileSelected;

        _rootDiv = _InitializeUI(accessToken, workspaceId, modelgroupId);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentModelfiles = [];

        if (_currentModelfilesHtmlListElement) {
            _currentModelfilesHtmlListElement = undefined;
        }

        if (_currentSelectedModelfileHtmlDivElement) {
            _currentSelectedModelfileHtmlDivElement.style.fontWeight = "";
            _currentSelectedModelfileHtmlDivElement.style.textDecoration = "";
            _currentSelectedModelfileHtmlDivElement = undefined;
        }
    }

    function _InitializeUI(accessToken: string, workspaceId: string, modelgroupId: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-modelfiles-root-div";

        let titleElement = document.createElement("h2");
        titleElement.innerText = "Modelfiles";
        rootDiv.appendChild(titleElement);

        // get button
        let getButton = document.createElement("button");
        getButton.id = "ui-modelfiles-get-button";
        getButton.textContent = "Get Modelfiles";
        rootDiv.appendChild(getButton);
        getButton.addEventListener("click", async () => {
            _currentModelfiles = await _FetchModelfiles(accessToken, workspaceId, modelgroupId);
            _currentModelfilesHtmlListElement = _RefreshUIModelfiles(_currentModelfilesHtmlListElement, _currentModelfiles);
        });

        // <br>
        let brElement = document.createElement("br");
        rootDiv.appendChild(brElement);

        // create input + label
        let createInput = document.createElement("input");
        createInput.id = "ui-modelfiles-create-input";
        let createLabelElement = document.createElement("label");
        createLabelElement.textContent = "Uploaded file identifier";
        createLabelElement.setAttribute("for", createInput.id);
        rootDiv.appendChild(createLabelElement);
        rootDiv.appendChild(createInput);

        // create button
        let createButton = document.createElement("button");
        createButton.id = "ui-modelfiles-create-button";
        createButton.textContent = "Create Modelfile";
        rootDiv.appendChild(createButton);
        createButton.addEventListener("click", async () => {
            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(createInput.value, { USE_PROFILES: { html: false } });
            await _CreateModelfile(accessToken, workspaceId, modelgroupId, sanitizedValue);
            _currentModelfiles = await _FetchModelfiles(accessToken, workspaceId, modelgroupId);
            _currentModelfilesHtmlListElement = _RefreshUIModelfiles(_currentModelfilesHtmlListElement, _currentModelfiles);
        });

        // list of modelfiles
        _currentModelfilesHtmlListElement = _CreateUIModelfiles(_currentModelfiles);
        rootDiv.appendChild(_currentModelfilesHtmlListElement);

        return rootDiv;
    }
    function _CreateUIModelfiles(modelfiles: IApiModelfile[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        const sortedModelfile: IApiModelfile[] = modelfiles.sort(
            (modelfileA, modelfileB) => new Date(modelfileA.created_at).getTime() - new Date(modelfileB.created_at).getTime()
        );

        for (let cModelfile of sortedModelfile) {
            let cListElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cModelDiv: HTMLDivElement = document.createElement("div");
            cModelDiv.id = cModelfile.id;
            cModelDiv.textContent = `modelfile ${cModelfile.id} | number of versions: ${cModelfile.versionIds.length} | created at: ${cModelfile.created_at}`;
            cListElement.appendChild(cModelDiv);
            cModelDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedModelfileHtmlDivElement) {
                    _currentSelectedModelfileHtmlDivElement.style.fontWeight = "";
                    _currentSelectedModelfileHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cModelDiv.style.fontWeight = "bold";
                cModelDiv.style.textDecoration = "underline";

                // Swap selected modelgroup
                _currentSelectedModelfileHtmlDivElement = cModelDiv;

                // Call next
                if (_onModelfileSelected) {
                    _onModelfileSelected(_accessToken, cModelfile.workspaceId, cModelfile.modelId, cModelfile.id);
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-modelfile-get-button-" + cModelfile.id;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const modelfile: IApiModelfile = await _FetchModelfile(_accessToken, cModelfile.workspaceId, cModelfile.modelId, cModelfile.id);
                _UpdateUIModelfilesFromModelfile(modelfile);
            });

            // delete button
            let cDeleteButton: HTMLButtonElement = document.createElement("button");
            cDeleteButton.id = "ui-modelfile-delete-button-" + cModelfile.id;
            cDeleteButton.textContent = "Delete";
            cDeleteButton.style.marginLeft = "10px";
            cListElement.appendChild(cDeleteButton);
            cDeleteButton.addEventListener("click", async () => {
                await _DeleteModelfile(_accessToken, cModelfile.workspaceId, cModelfile.modelId, cModelfile.id);
                _currentModelfiles = await _FetchModelfiles(_accessToken, cModelfile.workspaceId, cModelfile.modelId);
                _currentModelfilesHtmlListElement = _RefreshUIModelfiles(_currentModelfilesHtmlListElement, _currentModelfiles);
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIModelfiles(currentList?: HTMLUListElement, modelfiles: IApiModelfile[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIModelfiles(modelfiles);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIModelfilesFromModelfile(modelfile: IApiModelfile): void {
        const updatedModelfiles: IApiModelfile[] = _currentModelfiles.map(cModelfile => (modelfile.id === cModelfile.id ? modelfile : cModelfile));
        _currentModelfilesHtmlListElement = _RefreshUIModelfiles(_currentModelfilesHtmlListElement, updatedModelfiles);

        if (_currentSelectedModelfileHtmlDivElement) {
            _currentSelectedModelfileHtmlDivElement.style.fontWeight = "";
            _currentSelectedModelfileHtmlDivElement.style.textDecoration = "";
            _currentSelectedModelfileHtmlDivElement = undefined;
        }
    }

    async function _FetchModelfiles(accessToken: string, workspaceId: string, modelgroupId: string): Promise<IApiModelfile[]> {
        const apiModelfilesUrl: string = ApiRouteFormatter.FormatMeModelfilesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelgroupId);
        const getMeModelfilesResponse: { modelfiles: IApiModelfile[] } = await Network.GetMeModelfiles(apiModelfilesUrl, getApiKey(), accessToken);
        return getMeModelfilesResponse.modelfiles || [];
    }
    async function _CreateModelfile(accessToken: string, workspaceId: string, modelgroupId: string, fileId: string): Promise<IApiModelfile> {
        const apiModelfilesUrl: string = ApiRouteFormatter.FormatMeModelfilesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelgroupId);
        const createMeModelfileResponse: { modelfile: IApiModelfile } = await Network.PostMeModelfiles(
            apiModelfilesUrl,
            getApiKey(),
            accessToken,
            fileId
        );
        return createMeModelfileResponse.modelfile;
    }
    async function _FetchModelfile(accessToken: string, workspaceId: string, modelgroupId: string, modelfileId: string): Promise<IApiModelfile> {
        const apiModelfileUrl: string = ApiRouteFormatter.FormatMeModelfileUrl(
            isCloudRunEnabled(),
            getApiVersion(),
            workspaceId,
            modelgroupId,
            modelfileId
        );
        const getMeModelfileResponse: { modelfile: IApiModelfile } = await Network.GetMeModelfile(apiModelfileUrl, getApiKey(), accessToken);
        return getMeModelfileResponse.modelfile;
    }
    async function _DeleteModelfile(accessToken: string, workspaceId: string, modelgroupId: string, modelfileId: string): Promise<void> {
        const apiModelfileUrl: string = ApiRouteFormatter.FormatMeModelfileUrl(
            isCloudRunEnabled(),
            getApiVersion(),
            workspaceId,
            modelgroupId,
            modelfileId
        );
        await Network.DeleteMeModelfile(apiModelfileUrl, getApiKey(), accessToken);
    }
}
